import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QModal,
  QStack,
  QText,
  useToastProvider,
  QModalHeader,
  QModalBody,
  QModalActions,
  QButton,
} from '@qualio/ui-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import {
  DigitalSignatureInput,
  DigitalSignatureV2,
  DigitalSignatureV2CommentRequired,
  DigitalSignatureV2CommentRequiredSchema,
  DigitalSignatureV2Schema,
} from '../../types/digitalSignature';
import DigitalSignatureForm from './components/DigitalSignatureForm';
import { extractMessageFromError } from '../../utils/errorUtils';

type ModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSave: (payload: DigitalSignatureV2) => void;
  inputTexts: DigitalSignatureInput;
  isCommentRequired: boolean;
  onSubmitError?: (err: unknown) => boolean;
  isSubmitButtonDestructive?: boolean;
  emailPlaceholder?: string;
  passwordPlaceholder?: string;
  commentLabel?: string;
  commentPlaceholder?: string;
  displayToast?: boolean;
  isApprovalFlow?: boolean;
};

const DigitalSignatureModal = ({
  isOpen,
  setIsOpen,
  onSave,
  inputTexts,
  isCommentRequired,
  onSubmitError,
  isSubmitButtonDestructive = false,
  emailPlaceholder,
  passwordPlaceholder,
  commentLabel,
  commentPlaceholder,
  displayToast = true,
  isApprovalFlow = false,
}: ModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();

  const formMethods = useForm<DigitalSignatureV2 | DigitalSignatureV2CommentRequired>({
    mode: 'onSubmit',
    resolver: zodResolver(isCommentRequired ? DigitalSignatureV2CommentRequiredSchema : DigitalSignatureV2Schema),
    defaultValues: {
      email: '',
      password: '',
      comment: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    control,
  } = formMethods;
  const formHasErrors = !!Object.keys(errors).length;

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const onSubmit = useCallback(
    async (data: DigitalSignatureV2) => {
      try {
        const payload = { ...data, isApprovalFlow };
        await onSave(payload);
        setIsOpen(false);

        if (displayToast) {
          showToast({
            title: inputTexts.successToastHeader,
            description: inputTexts.successToastDescription,
            status: 'success',
            ...(inputTexts.successToastDataCy && { 'data-cy': inputTexts.successToastDataCy }),
          });
        }
        reset();
      } catch (err: any) {
        if (!onSubmitError?.(err) && displayToast) {
          let errorMessage = extractMessageFromError(err);
          if (errorMessage === 'Invalid email address; Invalid email or password') {
            // explicitly indicating email is not correct is a security smell, so we replace the message in this case
            errorMessage = t('common.errors.invalidCredentials');
          }
          showToast({
            title: t('common.statuses.failed'),
            description: errorMessage,
            status: 'error',
          });
        }
      }
    },
    [
      onSave,
      setIsOpen,
      displayToast,
      reset,
      showToast,
      inputTexts.successToastHeader,
      inputTexts.successToastDescription,
      inputTexts.successToastDataCy,
      onSubmitError,
      isApprovalFlow,
      t,
    ],
  );

  return (
    <QModal isOpen={isOpen} onClose={handleClose} size="xl">
      <QModalHeader>
        <QText>{inputTexts.headingText}</QText>
      </QModalHeader>
      <QModalBody pt={'2px'}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <QStack direction="column" color="gray.700" spacing={4}>
              <QText color="gray.800">{inputTexts.subText}</QText>
              <DigitalSignatureForm
                control={control}
                isCommentRequired={isCommentRequired}
                emailPlaceholder={emailPlaceholder}
                passwordPlaceholder={passwordPlaceholder}
                commentLabel={commentLabel}
                commentPlaceholder={commentPlaceholder}
              />
            </QStack>
            {/* Hidden submit button so password managers (e.g. 1Password) classify this
                as a login form and offer autofill, and to submit the form on hitting enter. */}
            <button hidden type="submit" />
          </form>
        </FormProvider>
      </QModalBody>
      <QModalActions>
        <QButton onClick={handleClose} variant="outline" isDisabled={isSubmitting}>
          Cancel
        </QButton>
        <QButton
          data-cy="submit-signature-modal"
          onClick={handleSubmit(onSubmit)}
          isDisabled={formHasErrors || isSubmitting}
          isLoading={isSubmitting}
          isDestructive={isSubmitButtonDestructive}
          isAI={false}
        >
          {inputTexts.submitButtonText}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default DigitalSignatureModal;
